/**
 * Utility functions for handling image URLs
 */

// Production server base URL
const PRODUCTION_BASE_URL = "https://services.webserviceco.com/inari-server";

/**
 * Replaces localhost URLs with production server URLs
 * @param {string} url - The image URL to process
 * @returns {string} - The processed URL with production server URL
 */
export const replaceLocalhostWithProduction = (url) => {
  if (!url || typeof url !== 'string') {
    return url;
  }

  // Replace localhost:8081 with production server URL
  return url.replace(
    /http:\/\/localhost:8081/g,
    PRODUCTION_BASE_URL
  );
};

/**
 * Processes an array of images and replaces localhost URLs
 * @param {Array} images - Array of image objects
 * @returns {Array} - Array of processed image objects
 */
export const processImageUrls = (images) => {
  if (!Array.isArray(images)) {
    return images;
  }

  return images.map(image => ({
    ...image,
    url: replaceLocalhostWithProduction(image.url)
  }));
};

/**
 * Processes a single product and replaces localhost URLs in all image fields
 * @param {Object} product - The product object
 * @returns {Object} - The processed product object
 */
export const processProductImages = (product) => {
  if (!product || typeof product !== 'object') {
    return product;
  }

  const processedProduct = { ...product };

  // Process main images array
  if (processedProduct.images && Array.isArray(processedProduct.images)) {
    processedProduct.images = processImageUrls(processedProduct.images);
  }

  // Process variants and their images
  if (processedProduct.variants && Array.isArray(processedProduct.variants)) {
    processedProduct.variants = processedProduct.variants.map(variant => ({
      ...variant,
      images: variant.images ? processImageUrls(variant.images) : variant.images
    }));
  }

  // Process any other image fields that might exist
  const imageFields = ['image', 'imageUrl', 'bannerImage', 'thumbnail'];
  imageFields.forEach(field => {
    if (processedProduct[field]) {
      processedProduct[field] = replaceLocalhostWithProduction(processedProduct[field]);
    }
  });

  return processedProduct;
};

/**
 * Processes an array of products and replaces localhost URLs
 * @param {Array} products - Array of product objects
 * @returns {Array} - Array of processed product objects
 */
export const processProductsImages = (products) => {
  if (!Array.isArray(products)) {
    return products;
  }

  return products.map(product => processProductImages(product));
};
